import _ from "lodash";
import axios from "axios";
import orderTypeEnum from "../../../enums/modules/orderTypeEnum";
import shippingMethodEnum from "../../../enums/modules/shippingMethodEnum";
import ShippingTypeEnum from "../../../enums/modules/shippingTypeEnum";
import AskEnum from "../../../enums/modules/askEnum";
import skydropxRateModeEnum from "../../../enums/modules/skydropxRateModeEnum";

// Dynamic Axios interceptor to read persisted store and inject the X-Cart-Token header
axios.interceptors.request.use(function (config) {
    const persistedStore = localStorage.getItem('vuex');
    if (persistedStore) {
        try {
            const storeObj = JSON.parse(persistedStore);
            if (storeObj && storeObj.frontendCart && storeObj.frontendCart.cart_token) {
                config.headers['X-Cart-Token'] = storeObj.frontendCart.cart_token;
            }
        } catch (e) {}
    }
    return config;
}, function (error) {
    return Promise.reject(error);
});

export const frontendCart = {
    namespaced: true,
    state: {
        lists: [],
        cart_token: null,
        subtotal: 0,
        total: 0,
        coupon: {},
        discount: 0,
        orderType: null,
        shippingAddress: {},
        billingAddress: {},
        outletAddress: {},
        paymentMethod: {},
        totalTax: 0,
        shippingCharge: 0,
        shippingQuotes: [],
        shippingRateMode: skydropxRateModeEnum.AUTO_MIN,
        selectedShippingQuote: null,
        isList: false,
        quoteCalculating: false,
        isFreeShippingActive: false,
    },
    getters: {
        lists: function (state) {
            return state.lists;
        },
        isExpiredAndOutOfStock: function (state) {
            return (item) => {
                if (!item.reserved_until) return false;
                const expired = new Date(item.reserved_until.replace(' ', 'T')).getTime() < new Date().getTime();
                const outOfStock = parseInt(item.stock) <= 0;
                return expired && outOfStock;
            }
        },
        activeLists: function (state, getters) {
            return state.lists.filter(item => !getters.isExpiredAndOutOfStock(item));
        },
        subtotal: function (state) {
            return state.subtotal;
        },
        coupon: function (state) {
            return state.coupon;
        },
        discount: function (state) {
            return state.discount;
        },
        total: function (state) {
            return state.total;
        },
        orderType: function (state) {
            return state.orderType;
        },
        shippingAddress: function (state) {
            return state.shippingAddress;
        },
        billingAddress: function (state) {
            return state.billingAddress;
        },
        outletAddress: function (state) {
            return state.outletAddress;
        },
        paymentMethod: function (state) {
            return state.paymentMethod;
        },
        totalTax: function (state) {
            return state.totalTax;
        },
        shippingCharge: function (state) {
            return state.shippingCharge;
        },
        shippingQuotes: function (state) {
            return state.shippingQuotes;
        },
        shippingRateMode: function (state) {
            return state.shippingRateMode;
        },
        selectedShippingQuote: function (state) {
            return state.selectedShippingQuote;
        },
        isList: function (state) {
            return state.isList;
        },
        quoteCalculating: function (state) {
            return state.quoteCalculating;
        },
        isFreeShippingActive: function (state) {
            return state.isFreeShippingActive;
        },
        cartToken: function (state) {
            if (!state.cart_token) {
                state.cart_token = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
                    var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
                    return v.toString(16);
                });
            }
            return state.cart_token;
        }
    },
    actions: {
        listChecker: function (context) {
            return new Promise((resolve, reject) => {
                if (context.state.lists.length > 0) {
                    context.commit('isList', true);
                    resolve({ status: true });
                } else {
                    context.commit('isList', false);
                    resolve({ status: false });
                }
                reject({
                    message: "no data found",
                    status: false
                });
            });
        },
        lists: function (context, payload) {
            return new Promise((resolve, reject) => {
                if (Object.keys(payload).length > 0) {
                    let isNew = false;
                    let productMatch = false;
                    if (context.state.lists.length === 0) {
                        isNew = true;
                    } else {
                        _.forEach(context.state.lists, (list, listKey) => {
                            if (list.product_id === payload.product_id && list.variation_id === payload.variation_id) {
                                productMatch = true;
                                if ((payload.quantity + list.quantity) <= list.stock) {
                                    if ((payload.quantity + list.quantity) <= list.maximum_purchase_quantity) {
                                        context.state.lists[listKey].quantity += payload.quantity;
                                        if (payload.reserved_until) {
                                            context.state.lists[listKey].reserved_until = payload.reserved_until;
                                        }
                                    } else {
                                        reject({
                                            message: "maximum_quantity",
                                            status: false
                                        });
                                    }
                                } else {
                                    reject({
                                        message: "stockOut",
                                        status: false
                                    });
                                }
                            }
                        });

                        if (!productMatch) {
                            isNew = true;
                        }
                        productMatch = false;
                    }

                    if (isNew) {
                        context.state.lists.push({
                            name: payload.name,
                            product_id: payload.product_id,
                            image: payload.image,
                            variation_names: payload.variation_names,
                            variation_id: payload.variation_id,
                            sku: payload.sku,
                            stock: payload.stock,
                            taxes: payload.taxes,
                            shipping: payload.shipping,
                            quantity: payload.quantity,
                            discount: payload.discount,
                            price: payload.price,
                            old_price: payload.old_price,
                            total_tax: 0,
                            subtotal: 0,
                            total: 0,
                            total_price: payload.total_price,
                            maximum_purchase_quantity: payload.maximum_purchase_quantity,
                            reserved_until: payload.reserved_until
                        });
                        isNew = false;
                    }
                }
                context.commit("taxCalculation");
                context.commit("shippingCharge", {
                    setting: context.rootState.frontendSetting.lists,
                    area: context.rootState.frontendOrderArea.lists
                });
                context.commit("subtotal");
                context.dispatch("quoteShippingCharge", { silent: true }).then().catch();
                context.dispatch('listChecker').then().catch();
                resolve({ data: context.state.lists, status: true });
            });
        },
        quantity: function (context, payload) {
            context.commit("quantity", payload);
            context.commit("taxCalculation");
            context.commit("shippingCharge", {
                setting: context.rootState.frontendSetting.lists,
                area: context.rootState.frontendOrderArea.lists
            });
            context.commit("subtotal");
            context.dispatch("quoteShippingCharge", { silent: true }).then().catch();
        },
        remove: function (context, payload) {
            const item = context.state.lists[payload.id];
            if (item) {
                axios.post('/frontend/cart/release', {
                    product_id: item.product_id,
                    variation_id: item.variation_id,
                    cart_token: context.getters.cartToken
                }).then().catch();
            }
            context.commit("remove", payload);
            context.commit("taxCalculation");
            context.commit("shippingCharge", {
                setting: context.rootState.frontendSetting.lists,
                area: context.rootState.frontendOrderArea.lists
            });
            context.commit("subtotal");
            context.dispatch("quoteShippingCharge", { silent: true }).then().catch();
            context.dispatch('listChecker').then().catch();
        },
        coupon: function (context, payload) {
            context.commit("coupon", payload);
            context.commit("subtotal");
        },
        destroyCoupon: function (context) {
            context.commit('coupon', {});
            context.commit("subtotal");
        },
        initOrderType: function (context, payload) {
            context.commit('orderTypeInit', payload);
            context.commit("shippingCharge", {
                setting: context.rootState.frontendSetting.lists,
                area: context.rootState.frontendOrderArea.lists
            });
            context.commit("subtotal");
            context.dispatch("quoteShippingCharge", { silent: true }).then().catch();
        },
        updateOrderType: function (context, payload) {
            context.commit('updateOrderType', payload);
            context.commit("shippingCharge", {
                setting: context.rootState.frontendSetting.lists,
                area: context.rootState.frontendOrderArea.lists
            });
            context.commit("subtotal");
            context.dispatch("quoteShippingCharge", { silent: true }).then().catch();
        },
        shippingAddress: function (context, payload) {
            context.commit('shippingAddress', payload);

            if (payload && payload.id) {
                context.commit('quoteCalculating', true);
                axios.get(`frontend/shipping-gateway/check-free-shipping?shipping_id=${payload.id}`)
                    .then((res) => {
                        const eligible = !!res.data.eligible;
                        context.commit('setIsFreeShippingActive', eligible);
                        context.commit("shippingCharge", {
                            setting: context.rootState.frontendSetting.lists,
                            area: context.rootState.frontendOrderArea.lists
                        });
                        context.commit("subtotal");
                        context.dispatch("quoteShippingCharge", { silent: true }).then().catch();
                    })
                    .catch(() => {
                        context.commit('setIsFreeShippingActive', false);
                        context.commit("shippingCharge", {
                            setting: context.rootState.frontendSetting.lists,
                            area: context.rootState.frontendOrderArea.lists
                        });
                        context.commit("subtotal");
                        context.dispatch("quoteShippingCharge", { silent: true }).then().catch();
                    });
            } else {
                context.commit('setIsFreeShippingActive', false);
                context.commit("shippingCharge", {
                    setting: context.rootState.frontendSetting.lists,
                    area: context.rootState.frontendOrderArea.lists
                });
                context.commit("subtotal");
                context.dispatch("quoteShippingCharge", { silent: true }).then().catch();
            }
        },
        billingAddress: function (context, payload) {
            context.commit('billingAddress', payload);
            context.commit("subtotal");
        },
        outletAddress: function (context, payload) {
            context.commit('outletAddress', payload);
            context.commit("subtotal");
        },
        paymentMethod: function (context, payload) {
            context.commit('paymentMethod', payload);
        },
        quoteShippingCharge: function (context, payload = {}) {
            return new Promise((resolve, reject) => {
                const setting = context.rootState.frontendSetting.lists;
                const isHybridMethod = setting.shipping_setup_method === shippingMethodEnum.HYBRID;
                const isSkydropxMethod = (
                    setting.shipping_setup_method === shippingMethodEnum.SKYDROPX ||
                    isHybridMethod
                );
                const hasShippingAddress = Object.keys(context.state.shippingAddress).length > 0;
                const hasProducts = context.state.lists.length > 0;
                const providerProducts = isHybridMethod
                    ? context.state.lists.filter((list) => parseInt(list?.shipping?.shipping_type ?? ShippingTypeEnum.GLOBAL) === ShippingTypeEnum.GLOBAL)
                    : context.state.lists;
                const hasProviderProducts = providerProducts.length > 0;
                const hybridManualCharge = () => {
                    if (!isHybridMethod) {
                        return 0;
                    }

                    let manualCharge = 0;
                    _.forEach(context.state.lists, (list) => {
                        if (list.shipping.shipping_type !== ShippingTypeEnum.FLAT_RATE) {
                            return;
                        }

                        const lineShippingCost = parseFloat(list.shipping.shipping_cost) || 0;
                        if (list.shipping.is_product_quantity_multiply === AskEnum.YES) {
                            manualCharge += (lineShippingCost * (parseInt(list.quantity) || 0));
                        } else {
                            manualCharge += lineShippingCost;
                        }
                    });

                    return manualCharge;
                };

                if (context.state.isFreeShippingActive) {
                    context.commit('setShippingQuotes', []);
                    context.commit('setSelectedShippingQuote', null);
                    context.commit('setShippingRateMode', skydropxRateModeEnum.AUTO_MIN);
                    context.commit('setShippingCharge', 0);
                    context.commit('subtotal');
                    context.commit('quoteCalculating', false);
                    resolve({ status: true, data: { shipping_charge: 0 } });
                    return;
                }

                if (!isSkydropxMethod || context.state.orderType !== orderTypeEnum.DELIVERY) {
                    context.commit('setShippingQuotes', []);
                    context.commit('setSelectedShippingQuote', null);
                    context.commit('setShippingRateMode', skydropxRateModeEnum.AUTO_MIN);
                    context.commit('quoteCalculating', false);
                    resolve({ status: true, data: { shipping_charge: 0 } });
                    return;
                }

                if (!hasShippingAddress || !hasProducts) {
                    context.commit('setShippingCharge', hybridManualCharge());
                    context.commit('subtotal');
                    context.commit('quoteCalculating', false);
                    resolve({ status: true, data: { shipping_charge: 0 } });
                    return;
                }

                if (isHybridMethod && !hasProviderProducts) {
                    context.commit('setShippingQuotes', []);
                    context.commit('setSelectedShippingQuote', null);
                    context.commit('setShippingRateMode', skydropxRateModeEnum.AUTO_MIN);
                    context.commit('setShippingCharge', hybridManualCharge());
                    context.commit('subtotal');
                    context.commit('quoteCalculating', false);
                    resolve({ status: true, data: { shipping_charge: hybridManualCharge() } });
                    return;
                }

                context.commit('quoteCalculating', true);
                axios.post('frontend/shipping-gateway/quote', {
                    shipping_id: context.state.shippingAddress.id,
                    products: providerProducts,
                }).then((res) => {
                    const previousSelectedQuoteId = context.state.selectedShippingQuote?.id ?? null;
                    let providerShippingCharge = parseFloat(res?.data?.data?.shipping_charge ?? 0);
                    const shippingQuotes = res?.data?.data?.quotes ?? [];
                    const shippingRateMode = parseInt(res?.data?.data?.rate_mode ?? skydropxRateModeEnum.AUTO_MIN);
                    let selectedQuote = res?.data?.data?.selected_quote ?? null;

                    if (shippingRateMode === skydropxRateModeEnum.CUSTOMER_SELECT && previousSelectedQuoteId !== null) {
                        const matchedQuote = shippingQuotes.find((quote) => String(quote.id) === String(previousSelectedQuoteId));
                        if (matchedQuote) {
                            selectedQuote = matchedQuote;
                            providerShippingCharge = parseFloat(matchedQuote.amount);
                        }
                    }

                    const shippingCharge = providerShippingCharge + hybridManualCharge();

                    context.commit('setShippingQuotes', shippingQuotes);
                    context.commit('setShippingRateMode', shippingRateMode);
                    context.commit('setSelectedShippingQuote', selectedQuote);
                    context.commit('setShippingCharge', shippingCharge);
                    context.commit('subtotal');
                    context.commit('quoteCalculating', false);
                    resolve(res);
                }).catch((err) => {
                    context.commit('setShippingCharge', 0);
                    context.commit('setShippingQuotes', []);
                    context.commit('setSelectedShippingQuote', null);
                    context.commit('subtotal');
                    context.commit('quoteCalculating', false);

                    if (!payload.silent) {
                        reject(err);
                        return;
                    }

                    resolve({ status: false, error: err });
                });
            });
        },
        selectShippingQuote: function (context, payload) {
            return new Promise((resolve) => {
                if (!payload || typeof payload.amount === 'undefined') {
                    resolve({ status: false });
                    return;
                }

                const setting = context.rootState.frontendSetting.lists;
                const isHybridMethod = setting.shipping_setup_method === shippingMethodEnum.HYBRID;
                let manualCharge = 0;

                if (isHybridMethod) {
                    _.forEach(context.state.lists, (list) => {
                        if (list.shipping.shipping_type !== ShippingTypeEnum.FLAT_RATE) {
                            return;
                        }

                        const lineShippingCost = parseFloat(list.shipping.shipping_cost) || 0;
                        if (list.shipping.is_product_quantity_multiply === AskEnum.YES) {
                            manualCharge += (lineShippingCost * (parseInt(list.quantity) || 0));
                        } else {
                            manualCharge += lineShippingCost;
                        }
                    });
                }

                context.commit('setSelectedShippingQuote', payload);
                context.commit('setShippingCharge', parseFloat(payload.amount) + manualCharge);
                context.commit('subtotal');
                resolve({ status: true });
            });
        },
        resetCart: function (context) {
            context.commit('resetCart');
        },
        checkCartStock: function (context) {
            if (context.state.lists.length === 0) return Promise.resolve();
            const items = context.state.lists.map(item => ({
                product_id: item.product_id,
                variation_id: item.variation_id
            }));
            return axios.post('/frontend/cart/check-stock', { items }).then(res => {
                if (res.data.status && res.data.data) {
                    context.commit('updateStocks', res.data.data);
                    context.commit('taxCalculation');
                    context.commit('shippingCharge', {
                        setting: context.rootState.frontendSetting.lists,
                        area: context.rootState.frontendOrderArea.lists
                    });
                    context.commit('subtotal');
                }
            }).catch();
        },
    },
    mutations: {
        subtotal: function (state) {
            state.total = 0;
            if (state.lists.length > 0) {
                let subtotal = 0;
                let total = 0;
                _.forEach(state.lists, (list, listKey) => {
                    const expired = list.reserved_until && new Date(list.reserved_until.replace(' ', 'T')).getTime() < new Date().getTime();
                    const outOfStock = parseInt(list.stock) <= 0;
                    if (expired && outOfStock) {
                        state.lists[listKey].subtotal = 0;
                        state.lists[listKey].total = 0;
                        return;
                    }
                    state.lists[listKey].subtotal = state.lists[listKey].price * state.lists[listKey].quantity;
                    state.lists[listKey].total = ((state.lists[listKey].price * state.lists[listKey].quantity) + state.lists[listKey].total_tax) - state.lists[listKey].discount;
                    subtotal += state.lists[listKey].subtotal;
                    total += state.lists[listKey].total;
                });
                state.subtotal = subtotal;
                state.total = total;
            } else {
                state.subtotal = 0;
                state.total = 0;
            }

            if (state.shippingCharge > 0) {
                state.total += state.shippingCharge;
            }

            if (Object.keys(state.coupon).length > 0) {
                state.total -= state.coupon.convert_discount;
            }
        },
        quantity: function (state, payload) {
            if (payload.status === "increment") {
                state.lists[payload.id].quantity++;
            } else if (payload.status === "decrement") {
                if (state.lists[payload.id].quantity !== 1) {
                    state.lists[payload.id].quantity--;
                }
            } else {
                state.lists[payload.id].quantity = payload.status;
            }

            state.lists[payload.id].total_price = state.lists[payload.id].price * state.lists[payload.id].quantity;
        },
        remove: function (state, payload) {
            state.lists.splice(payload.id, 1);
        },
        coupon: function (state, payload) {
            state.coupon = payload;
            if (Object.keys(payload).length > 0) {
                state.discount = payload.convert_discount;
            } else {
                state.discount = 0;
            }
        },
        orderTypeInit: function (state, payload) {
            if (state.orderType === null) {
                state.orderType = payload.order_type;
            }
        },
        updateOrderType: function (state, payload) {
            if (orderTypeEnum.DELIVERY === payload || orderTypeEnum.PICK_UP === payload) {
                state.orderType = payload;
            } else {
                state.orderType = null;
            }
        },
        shippingAddress: function (state, payload) {
            state.shippingAddress = payload;
        },
        billingAddress: function (state, payload) {
            state.billingAddress = payload;
        },
        outletAddress: function (state, payload) {
            state.outletAddress = payload;
        },
        paymentMethod: function (state, payload) {
            state.paymentMethod = payload;
        },
        setShippingCharge: function (state, payload) {
            state.shippingCharge = parseFloat(payload);
        },
        setShippingQuotes: function (state, payload) {
            state.shippingQuotes = payload;
        },
        setShippingRateMode: function (state, payload) {
            state.shippingRateMode = parseInt(payload);
        },
        setSelectedShippingQuote: function (state, payload) {
            state.selectedShippingQuote = payload;
        },
        quoteCalculating: function (state, payload) {
            state.quoteCalculating = payload;
        },
        taxCalculation: function (state) {
            let stateTotalTax = 0;
            _.forEach(state.lists, (list, listKey) => {
                const expired = list.reserved_until && new Date(list.reserved_until.replace(' ', 'T')).getTime() < new Date().getTime();
                const outOfStock = parseInt(list.stock) <= 0;
                if (expired && outOfStock) {
                    state.lists[listKey].total_tax = 0;
                    return;
                }
                if (list.taxes.length > 0) {
                    let taxes = [];
                    let total_tax = 0;
                    _.forEach(list.taxes, (tax, taxKey) => {
                        if (tax.tax_rate > 0) {
                            let taxPercentagePrice = ((list.price / 100) * parseFloat(tax.tax_rate));
                            total_tax += taxPercentagePrice;
                            taxes.push({
                                id: tax.id,
                                name: tax.name,
                                code: tax.code,
                                tax_rate: parseFloat(tax.tax_rate),
                                tax_amount: parseFloat(taxPercentagePrice)
                            })
                        }
                    });
                    state.lists[listKey].taxes = taxes;
                    state.lists[listKey].total_tax = (total_tax * state.lists[listKey].quantity);
                }
                stateTotalTax += state.lists[listKey].total_tax;
            });
            state.totalTax = stateTotalTax;
        },
        shippingCharge: function (state, payload) {
            if (state.orderType === orderTypeEnum.DELIVERY) {
                if (state.isFreeShippingActive) {
                    state.shippingCharge = 0;
                    return;
                }
                const totalQuantity = _.sumBy(state.lists, (list) => {
                    const expired = list.reserved_until && new Date(list.reserved_until.replace(' ', 'T')).getTime() < new Date().getTime();
                    const outOfStock = parseInt(list.stock) <= 0;
                    if (expired && outOfStock) {
                        return 0;
                    }
                    return parseInt(list.quantity) || 0;
                });

                if (payload.setting.shipping_setup_method === shippingMethodEnum.FLAT_RATE_WISE) {
                    const flatRateCost = parseFloat(payload.setting.shipping_setup_flat_rate_wise_cost) || 0;
                    if (payload.setting.shipping_setup_flat_rate_multiply_per_item) {
                        state.shippingCharge = flatRateCost * totalQuantity;
                    } else {
                        state.shippingCharge = flatRateCost;
                    }
                } else if (payload.setting.shipping_setup_method === shippingMethodEnum.PRODUCT_WISE) {
                    let totalShippingCost = 0;
                    _.forEach(state.lists, (list, listKey) => {
                        const expired = list.reserved_until && new Date(list.reserved_until.replace(' ', 'T')).getTime() < new Date().getTime();
                        const outOfStock = parseInt(list.stock) <= 0;
                        if (expired && outOfStock) {
                            return;
                        }
                        if (list.shipping.shipping_type === ShippingTypeEnum.FLAT_RATE) {
                            if (list.shipping.is_product_quantity_multiply === AskEnum.YES) {
                                totalShippingCost += (parseFloat(list.shipping.shipping_cost) * list.quantity);
                            } else {
                                totalShippingCost += (parseFloat(list.shipping.shipping_cost));
                            }
                        }
                    });
                    state.shippingCharge = totalShippingCost;
                } else if (payload.setting.shipping_setup_method === shippingMethodEnum.AREA_WISE) {
                    if (Object.keys(state.shippingAddress).length > 0) {
                        let status = false;
                        let areaCost = parseFloat(payload.setting.shipping_setup_area_wise_default_cost) || 0;

                        _.forEach(payload.area, (list, listKey) => {
                            if (list.country === state.shippingAddress.country && list.state === state.shippingAddress.state && list.city === state.shippingAddress.city) {
                                status = true;
                                areaCost = parseFloat(list.shipping_cost) || 0;
                            }
                        });

                        if (!status) {
                            areaCost = parseFloat(payload.setting.shipping_setup_area_wise_default_cost) || 0;
                        }

                        if (payload.setting.shipping_setup_area_wise_multiply_per_item) {
                            state.shippingCharge = areaCost * totalQuantity;
                        } else {
                            state.shippingCharge = areaCost;
                        }
                    }
                } else if (
                    payload.setting.shipping_setup_method === shippingMethodEnum.SKYDROPX ||
                    payload.setting.shipping_setup_method === shippingMethodEnum.HYBRID
                ) {
                    state.shippingCharge = 0;
                }
            } else {
                state.shippingCharge = 0;
            }
        },
        isList: function (state, payload) {
            state.isList = payload;
        },
        updateStocks: function (state, payload) {
            _.forEach(payload, (resItem) => {
                const index = state.lists.findIndex(list => 
                    list.product_id === resItem.product_id && 
                    list.variation_id === resItem.variation_id
                );
                if (index !== -1) {
                    state.lists[index].stock = resItem.stock;
                }
            });
        },
        resetCart: function (state) {
            state.lists = [];
            state.subtotal = 0;
            state.total = 0;
            state.coupon = {};
            state.discount = 0;
            state.shippingAddress = {};
            state.billingAddress = {};
            state.outletAddress = {};
            state.paymentMethod = {};
            state.totalTax = 0;
            state.shippingCharge = 0;
            state.shippingQuotes = [];
            state.shippingRateMode = skydropxRateModeEnum.AUTO_MIN;
            state.selectedShippingQuote = null;
            state.quoteCalculating = false;
            state.isFreeShippingActive = false;
        },
        setIsFreeShippingActive: function (state, payload) {
            state.isFreeShippingActive = payload;
        }
    },
};
